import * as C from '../common/constants';
import * as ROUTES from '../common/routes';
import Button from './Button';
import DropZoneContainer from './DropZoneContainer';
import InputComponent from './InputComponent';
import React, { useEffect, useRef, useState } from 'react';
import SurveyAccordion from './SurveyAccordion';
import SurveyFormInfo from './SurveyFormInfo';
import SurveyFormPersonalContacts from './SurveyFormPersonalContacts';
import SurveyFormPersonalName from './SurveyFormPersonalName';
import SurveyFormPublicDialog from './SurveyFormPublicDialog';
import SurveyFormRadioButtons from './SurveyFormRadioButtons';
import UploadPreview from './UploadPreview';
import { COLORS, FONTS, FONT_WEIGHT } from '../common/theme';
import { EIDs, EStoryType } from '../common/enums';
import { getText, getPath } from '../common/utils/helpers';
import { makeStyles, createStyles, StyleRules } from '@mui/styles';
import { sendFormData, showSuccess } from '../common/app/actions';
import { useDynamicTranslations } from '../common/hooks/useTranslations';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { showLoginPopup } from '../common/app/actions';
import { db } from '../config';
import {
  getFirestore,
  collection,
  addDoc,
  doc,
  setDoc,
  getDoc,
} from 'firebase/firestore';

const INTERVIEW = [
  {
    placeholder: 'vards',
    safeName: 'intervejama_vards',
    isMandatory: true,
    isSwitchVisible: true,
    isSwitchChecked: true,
    isSwitchDisabled: true,
  },
  {
    placeholder: 'uzvards',
    safeName: 'intervejama_uzvards',
    isMandatory: true,
    isSwitchVisible: true,
    isSwitchChecked: false,
  },
  {
    placeholder: 'dzimsanas_gads',
    safeName: 'intervejama_dzimsanas_gads',
    isMandatory: true,
    isSwitchVisible: true,
    isSwitchChecked: false,
  },
  {
    placeholder: 'dzimsanas_vieta',
    safeName: 'intervejama_dzimsanas_vieta',
    isMandatory: true,
    isSwitchVisible: true,
    isSwitchChecked: false,
  },
  {
    placeholder: 'intervijas_laiks',
    safeName: 'intervijas_laiks',
    isMandatory: true,
    isSwitchVisible: true,
    isSwitchChecked: false,
  },
  {
    placeholder: 'intervijas_vieta',
    safeName: 'intervijas_vieta',
    isMandatory: true,
    isSwitchVisible: true,
    isSwitchChecked: false,
  },
];

const PLACE_AND_TIME = [
  {
    placeholder: 'place',
    safeName: 'place',
  },
  {
    placeholder: 'time',
    safeName: 'time',
  },
];

const useStyles = makeStyles(
  (theme): StyleRules =>
    createStyles({
      centerContainerStyle: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '30vh',
        flex: 1,
      },
      personalDataStyle: {
        width: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
      },
      placeAndTimeStyle: {
        width: '100%',
        padding: '6px 10px',
        marginTop: '10px',
      },
      subLabelStyle: {
        width: '100%',
        // whiteSpace: 'nowrap',
        margin: '10px 0',
        fontWeight: FONT_WEIGHT.medium,
        fontSize: '16px',
        fontFamily: FONTS.alegreya,
        color: COLORS.appBlue,
      },
      inputTextAreaStyle: {
        width: '100%',
      },
      bottomContainerStyle: {
        position: 'relative',
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        marginTop: '40px',
        paddingLeft: '30px',
        boxSizing: 'border-box',
      },
      buttonsContainerStyle: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'flex-end',
        flexDirection: 'row',
        width: '100%',
      },
      buttonsWrapperStyle: {
        display: 'flex',
        // alignItems: 'flex-start',
        flexDirection: 'column',
      },
      spacingStyle: {
        width: '100%',
        height: '2px',
        margin: '4px 0',
      },
      doubleSpacingStyle: {
        width: '100%',
        height: '2px',
        margin: '10px 0',
      },
      tripleSpacingStyle: {
        width: '100%',
        height: '2px',
        margin: '14px 0',
      },
      verticalSpacingStyle: {
        width: '20px',
      },
      spacingTenStyle: {
        width: '100%',
        height: '2px',
        margin: '10px 0',
      },
      personalStyle: {
        width: 'calc(50% - 4px)',
        padding: '6px 10px',
        marginBottom: '10px',
      },
      errorStyle: {
        position: 'absolute',
        bottom: '-18px',
        right: 0,
        fontWeight: FONT_WEIGHT.medium,
        fontSize: '14px',
        fontFamily: FONTS.alegreya,
        color: COLORS.red,
      },
      buttonBoxStyle: {
        position: 'relative',
      },
      '@global': {
        '.MuiFormControlLabel-label': {
          fontSize: '14px',
          fontFamily: `${FONTS.alegreya} !important`,
          color: COLORS.appBlue,
        },
        '.MuiTooltip-tooltip': {
          fontSize: '16px',
          lineHeight: '20px',
          padding: '12px',
        },
      },
      saveButtonStyle: {
        padding: '0 20px',
      },
      [`@media (max-width: 560px)`]: {
        bottomContainerStyle: {
          marginTop: '20px',
        },
        buttonsContainerStyle: {
          flexDirection: 'column',
        },
      },
      [`@media (max-width: 520px)`]: {
        personalDataStyle: {
          width: '100%',
          display: 'flex',
          flexWrap: 'unset',
          // justifyContent: 'space-between',
          flexDirection: 'column',
        },
        personalStyle: {
          width: '100%',
          padding: '6px 10px',
          marginBottom: '10px',
        },
      },
    })
);

const useArrayRef = () => {
  const refs = [];
  return [refs, (el) => el && refs.push(el)];
};

const SurveyForm = ({ data: surveyData, isAgreementChecked }): JSX.Element => {
  const data = surveyData?.pages;

  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { themeId, surveyId } = useParams();
  const lng = useSelector((state: any) => state.app.lng);
  const lngType = surveyData?.content?.type || lng;
  const authData = useSelector((state: any) => state.app.authData);
  const draftsData = useSelector((state: any) => state.app.draftsData);

  const myinfoLabel = useDynamicTranslations('myinfo', lngType);
  const contactmeLabel = useDynamicTranslations('contactme', lngType);
  const interviewinfoLabel = useDynamicTranslations('interviewinfo', lngType);
  const howgotLabel = useDynamicTranslations('howgot', lngType);
  const submitLabel = useDynamicTranslations('submit', lngType);
  const saveLabel = useDynamicTranslations('save', lngType);
  const successLabel = useDynamicTranslations('success', lngType);

  const [radioValue, setRadioValue] = useState(EStoryType.MYSELF);

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [fileComments, setFileComments] = useState({});

  const [isProgress, setProgress] = useState(false);
  const [elements, ref] = useArrayRef();

  const [isRequiredError, setRequiredError] = useState(false);

  const [isDialogOpen, setDialog] = useState(false);
  const [isPublicChecked, setPublicChecked] = useState(true);

  useEffect(() => {
    if (isRequiredError) {
      const timer = setTimeout(() => {
        setRequiredError(false);
      }, 4000);
    }
  }, [isRequiredError]);

  const getPlaceAndTime = (item, passedIndex, parentName) => {
    const hasPlaceAndTime = item?.content?.precision === 'true';

    if (!hasPlaceAndTime) {
      return null;
    }

    return (
      <>
        {PLACE_AND_TIME.map((options, index) => {
          const dataName = `answer_${options.safeName}${passedIndex + 1}`;

          const defaultValue = getDefaultValue(options?.safeName, parentName);

          return (
            <InputComponent
              className={classes.placeAndTimeStyle}
              placeholder={options.placeholder}
              dataName={dataName}
              options={options}
              maxLength={C.MAX_TEXT_INPUT}
              passedRef={ref}
              type="text"
              key={index}
              isRemainingVisible
              lng={lngType}
              {...(defaultValue ? { defaultValue } : {})}
            />
          );
        })}
      </>
    );
  };

  const getDefaultValue = (itemKey, question) => {
    const draftTheme = draftsData?.[themeId];
    const draftSurvey = draftTheme?.data?.[surveyId];
    const surveyDraftData = draftSurvey ? JSON.parse(draftSurvey) : '';

    const answerDraftData = surveyDraftData?.data?.find(
      (item) => item?.question === question
    );

    return answerDraftData?.[itemKey] ? answerDraftData?.[itemKey] : '';
  };

  const getQuestions = (item, index) => {
    const parentName: string = getText(item?.content, 'text', lng);
    const answerId = `answer${index + 1}`;
    const isDefaultExpanded = index === 0;

    const defaultValue = getDefaultValue('answer', parentName);

    return (
      <SurveyAccordion
        title={parentName}
        key={index}
        defaultExpanded={isDefaultExpanded}
      >
        <InputComponent
          className={classes.inputTextAreaStyle}
          placeholder={''}
          parentName={parentName}
          dataName={answerId}
          maxLength={C.MAX_TEXT_AREA_INPUT}
          passedRef={ref}
          lng={lngType}
          {...(defaultValue ? { value: defaultValue } : {})}
        />
        {getPlaceAndTime(item, index, parentName)}
        <div className={classes.spacingTenStyle} />
      </SurveyAccordion>
    );
  };

  const onRadioChange = (event) => {
    setRadioValue(event.target.value);
  };

  const isInterview: boolean = radioValue === EStoryType.INTERVIEW;
  const isOther: boolean = radioValue === EStoryType.OTHER;
  const hasExtra: boolean = isInterview || isOther;
  const getExtra = () => {
    if (isInterview) {
      return (
        <>
          <div className={classes.subLabelStyle}>{interviewinfoLabel}</div>
          {INTERVIEW.map((options, index) => {
            return (
              <InputComponent
                className={classes.personalStyle}
                placeholder={options.placeholder}
                dataName={options.safeName}
                options={options}
                maxLength={C.MAX_TEXT_INPUT}
                passedRef={ref}
                type="text"
                key={index}
                lng={lngType}
              />
            );
          })}
        </>
      );
    }

    return (
      <>
        <div className={classes.subLabelStyle}>{howgotLabel}</div>
        <InputComponent
          className={classes.inputTextAreaStyle}
          placeholder={''}
          dataName={'story'}
          maxLength={C.MAX_TEXT_AREA_INPUT}
          passedRef={ref}
          lng={lngType}
        />
      </>
    );
  };

  const getSurvey = (elementItem, elementKey) => {
    const elementParentKey = elementItem?.getAttribute('data-parent-name');
    const answerNumber = elementKey.replace('answer', '');
    const placeElement = (elements as any[]).find((item) => {
      const itemKey = item?.getAttribute('data-name');

      return itemKey === `answer_place${answerNumber}`;
    });

    const timeElement = (elements as any[]).find((item) => {
      const itemKey = item?.getAttribute('data-name');

      return itemKey === `answer_time${answerNumber}`;
    });

    const survey = {
      question: elementParentKey,
      answer: elementItem?.value,
      place: placeElement?.value,
      time: timeElement?.value,
    };

    return survey;
  };

  const getSurveys = () => {
    const surveys: any[] = [];

    (elements as any[]).forEach((elementItem) => {
      const elementKey = elementItem?.getAttribute('data-name');

      // if its place or time addition, then skip
      if (elementKey.includes('answer') && !elementKey.includes('answer_')) {
        const survey = getSurvey(elementItem, elementKey);

        if (survey?.answer) {
          surveys.push(survey);
        }
      }
    });

    return surveys;
  };

  const getFormData = () => {
    const formData = new FormData();

    (elements as any[]).forEach((elementItem) => {
      const elementKey = elementItem?.getAttribute('data-name');
      const inputType = elementItem?.getAttribute('data-type');

      if (!elementKey.includes('answer')) {
        if (inputType === 'text') {
          const isPublic = elementItem?.getAttribute('data-show-public');
          const inputData = {
            value: elementItem?.value,
            public: isPublic,
          };
          const inputDataJSON = JSON.stringify(inputData);
          formData.append(elementKey, inputDataJSON); // elementItem?.value
        } else {
          formData.append(elementKey, elementItem?.value);
        }
      }
    });

    const showpublic = isPublicChecked ? 'true' : 'false';

    const surveys = getSurveys();
    const surveysAsJSONstring = JSON.stringify(surveys);
    formData.append('survey', surveysAsJSONstring);
    formData.append('themeId', themeId);
    formData.append('surveyId', surveyId);
    formData.append('showpublic', showpublic);

    uploadedFiles.forEach((file: any, index) => {
      const uuid = file.uuid;
      const fileComment = fileComments[uuid] || '';
      formData.append(`comment[${index}]`, fileComment);
      formData.append(`file[${index}]`, file);
    });

    return formData;
  };

  const onSave = async (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();

    const themeLabel = surveyData?.parent.content[`title_${lng}`];
    const surveyLabel = surveyData?.content[`title_${lng}`];

    if (!authData) {
      dispatch(showLoginPopup());
    } else {
      const userRef = doc(db, 'users', authData?.uid);
      const surveys = getSurveys();

      const dataForSave = {
        data: surveys,
        label: surveyLabel,
      };
      const dataAsJSONstring = JSON.stringify(dataForSave);
      const currentSurvey = [dataAsJSONstring];

      try {
        const docSnap = await getDoc(userRef);
        const drafts = docSnap.data()?.drafts || {};

        const currentTheme = drafts?.[themeId] || {};
        const updatedTheme = {
          data: {
            ...currentTheme.data,
            [surveyId]: currentSurvey,
          },
          label: themeLabel,
        };

        const updatedDrafts = {
          ...drafts,
          [themeId]: updatedTheme,
        };

        await setDoc(userRef, {
          drafts: updatedDrafts,
        });
      } catch (error) {
        console.log(error);
      } finally {
        dispatch(showSuccess('Anketa saglabāta pie melnrakstiem'));
        navigate(ROUTES.ROOT);
      }
    }
  };

  const onSend = async (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();

    setProgress(true);

    const formData = getFormData();

    try {
      const data: string = await dispatch(sendFormData(formData));

      // console.log('>>>>', data);

      if (data === 'success') {
        dispatch(showSuccess(successLabel));
        navigate(ROUTES.ROOT);
      }
    } catch (e) {
      console.log('error', e);
    } finally {
      setProgress(false);
    }
  };

  const onFilesAdd = (acceptedFiles: File[]) => {
    setUploadedFiles((prevState) => {
      const currentFiles = [...prevState, ...acceptedFiles];

      return currentFiles;
    });
  };

  const onAddComment = (uuid, comment) => {
    setFileComments((prevState) => {
      const currentFileComments = { ...prevState, [uuid]: comment };

      return currentFileComments;
    });
  };

  const MANDATORY_FIELDS = ['vards', 'uzvards', 'dzimsanas_gads', 'email'];
  const MANDATORY_OTHER_FIELDS = [
    'intervejama_vards',
    'intervejama_uzvards',
    'intervejama_dzimsanas_gads',
    'intervejama_dzimsanas_vieta',
    'intervijas_laiks',
    'intervijas_vieta',
  ];

  const getRequiredFields = () => {
    const mandatory = [];

    (elements as any[]).forEach((elementItem) => {
      const elementKey = elementItem?.getAttribute('data-name');

      if (MANDATORY_FIELDS.includes(elementKey)) {
        mandatory.push(Boolean(elementItem?.value));
      }

      if (MANDATORY_OTHER_FIELDS.includes(elementKey)) {
        mandatory.push(Boolean(elementItem?.value));
      }
    });

    const isValid = mandatory.every((item) => item);

    return isValid;
  };

  const onSubmitClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();

    const isRequiredFields = getRequiredFields();

    if (isRequiredFields) {
      setDialog(true);
    } else {
      setRequiredError(true);
    }
  };

  const onClosePublicDialog = (event?: React.SyntheticEvent) => {
    event?.stopPropagation();
    event?.preventDefault();

    setDialog(false);
  };

  const onCheckboxChange = (event) => {
    setPublicChecked(event.target.checked);
  };

  const addTooltipLabel = useDynamicTranslations('addtooltip', lng);
  const sendTooltipLabel = useDynamicTranslations('sendtooltip', lng);
  const saveTooltipLabel = useDynamicTranslations('savetooltip', lng);

  return (
    <>
      <SurveyAccordion title={myinfoLabel} expanded>
        <div className={classes.personalDataStyle}>
          <SurveyFormPersonalName passedRef={ref} lng={lngType} />
          <SurveyFormPersonalContacts
            passedRef={ref}
            lng={lngType}
            label={contactmeLabel}
          />
          <span className={classes.spacingStyle} />
          <SurveyFormRadioButtons
            onRadioChange={onRadioChange}
            radioValue={radioValue}
            lng={lngType}
          />
          <span className={classes.spacingStyle} />
          {hasExtra && getExtra()}
        </div>
      </SurveyAccordion>
      <span className={classes.spacingStyle} />

      {data.map(getQuestions)}

      <div className={classes.bottomContainerStyle}>
        <div className={classes.buttonsContainerStyle}>
          <UploadPreview
            files={uploadedFiles}
            onAddComment={onAddComment}
            fileComments={fileComments}
          />
          <div className={classes.buttonsWrapperStyle}>
            <div className={classes.buttonBoxStyle}>
              <SurveyFormInfo label={addTooltipLabel} />
              <DropZoneContainer onFilesAdd={onFilesAdd} lng={lngType} />
            </div>
            <span className={classes.tripleSpacingStyle} />
            <div className={classes.buttonBoxStyle}>
              <SurveyFormInfo label={sendTooltipLabel} />
              <Button
                onClick={onSubmitClick}
                isProgress={isProgress}
                text={submitLabel}
                disabled={!isAgreementChecked}
              />
              <span className={classes.errorStyle}>
                {isRequiredError
                  ? '* Lūdzu aizpildiet visus obligātos ievades laukus'
                  : ''}
              </span>
            </div>
            <span className={classes.tripleSpacingStyle} />
            <div className={classes.buttonBoxStyle}>
              <SurveyFormInfo label={saveTooltipLabel} />
              <Button
                onClick={onSave}
                isProgress={isProgress}
                text={saveLabel}
                disabled={!isAgreementChecked}
                className={classes.saveButtonStyle}
              />
            </div>
          </div>
        </div>
      </div>
      <SurveyFormPublicDialog
        onClose={onClosePublicDialog}
        onCheckboxChange={onCheckboxChange}
        isPublicChecked={isPublicChecked}
        isProgress={isProgress}
        isDialogOpen={isDialogOpen}
        onSend={onSend}
        lng={lngType}
      />
    </>
  );
};

export default SurveyForm;
